import { Button, Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight } from "lucide-react";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatCurrency } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { AffordabilityRow } from "web_myplann/Inspiration/Components/AffordabilityRow";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import { INSPIRATION_CURRENCY } from "web_myplann/Inspiration/constants";
import type { OwnHomeContext } from "web_myplann/Inspiration/Utils/ownHomeMath";

const R = Resources.Inspiration.OwnHome.Result;

type Props = {
	context: OwnHomeContext;
	onEdit: () => void;
};

export const AffordabilitySection: FC<Props> = ({ context, onEdit }) => {
	const { t } = useResource();
	const theme = useTheme();

	const isFull = context.state === "full";
	const maxMortgage = context.maxMortgage ?? 0;
	const maxPayment = context.maxPayment ?? 0;
	const income = context.income ?? 0;

	return (
		<InspirationSection
			title={t(R.affordabilityTitle)}
			rightAction={
				<Button
					variant="textLink"
					size="small"
					endIcon={<ChevronRight size={theme.iconSize.xxs} />}
					onClick={onEdit}
				>
					{t(R.edit)}
				</Button>
			}
		>
			<Stack
				bgcolor={theme.palette.surface.muted}
				borderRadius={theme.radii.lg}
				px={1.6}
				py={1.2}
			>
				<Typography variant="button" color={theme.palette.grey[500]}>
					{isFull ? t(R.affordabilityExact) : t(R.affordabilityApprox)}
				</Typography>
				<Typography variant="h1" tabular mt={0.4}>
					~{formatCurrency(maxMortgage, INSPIRATION_CURRENCY, 0)}
				</Typography>
				<Typography variant="body2" mt={0.2}>
					{t(R.maxMortgageLabel)}
				</Typography>

				<Stack
					mt={1.2}
					pt={1.2}
					gap={0.6}
					borderTop={theme.colors.border.solid(theme.palette.grey[200])}
				>
					<AffordabilityRow
						label={t(R.rowMaxPayment)}
						value={`~${formatCurrency(maxPayment, INSPIRATION_CURRENCY, 0)}`}
					/>
					<AffordabilityRow
						label={t(R.rowIncome)}
						value={formatCurrency(income, INSPIRATION_CURRENCY, 0)}
					/>
					{isFull && (
						<>
							<AffordabilityRow
								label={t(R.rowExpenses)}
								value={formatCurrency(
									context.expenses ?? 0,
									INSPIRATION_CURRENCY,
									0,
								)}
							/>
							{context.cashflow !== undefined && (
								<AffordabilityRow
									label={t(R.rowCashflow)}
									value={formatCurrency(
										context.cashflow,
										INSPIRATION_CURRENCY,
										0,
									)}
								/>
							)}
						</>
					)}
				</Stack>
			</Stack>

			<Typography variant="hint" color={theme.palette.grey[400]} mt={0.8}>
				{isFull
					? t(R.affordabilityFootnoteExact)
					: t(R.affordabilityFootnoteApprox)}
			</Typography>
		</InspirationSection>
	);
};
